import React, { useState } from 'react';
import { arrayOf, bool, func, object, oneOfType, shape, string } from 'prop-types';

// Import contexts and util modules
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { propTypes, LINE_ITEM_HOUR, DATE_TYPE_DATE, DATE_TYPE_DATETIME } from '../../util/types';
import { ensureTransaction } from '../../util/data';
import { createSlug } from '../../util/urlHelpers';
import { isTransactionInitiateListingNotFoundError } from '../../util/errors';
import { convertMoneyToNumber } from '../../util/currency';
import { getProcess, isBookingProcessAlias } from '../../transactions/transaction';
import { formatListingForAnalytics, trackEvent } from '../../util/analytics';
import { IoShieldCheckmark } from 'react-icons/io5';
import { FaHandshake } from 'react-icons/fa6';
// Import shared components
import { H3, H4, IconSpinner, Modal, NamedLink, OrderBreakdown, Page } from '../../components';
import refundImage from '../../assets/coins-euro-money-currency-finance-payment-svgrepo-com.svg';
import {
  bookingDatesMaybe,
  getBillingDetails,
  getFormattedTotalPrice,
  getShippingDetailsForSave,
  getShippingDetailsMaybe,
  getTransactionTypeData,
  hasDefaultPaymentMethod,
  hasPaymentExpired,
  hasTransactionPassedPendingPayment,
  processCheckoutWithPayment,
  setOrderPageInitialValues,
} from './CheckoutPageTransactionHelpers.js';
import { getErrorMessages } from './ErrorMessages';

import CustomTopbar from './CustomTopbar';
import StripePaymentForm from './StripePaymentForm/StripePaymentForm';
import DetailsSideCard from './DetailsSideCard';
import MobileListingImage from './MobileListingImage';
import MobileOrderBreakdown from './MobileOrderBreakdown';

import css from './CheckoutPage.module.css';
import PurchaseType from './PurchaseType.js';
import {
  PRIVACY_POLICY_ASSET_NAME,
  TOS_ASSET_NAME,
} from '../AuthenticationPage/AuthenticationPage.duck.js';
import { camelize } from '../../util/string.js';
import { PrivacyPolicyContent } from '../PrivacyPolicyPage/PrivacyPolicyPage.js';
import { TermsOfServiceContent } from '../TermsOfServicePage/TermsOfServicePage.js';
import directOne from '../../assets/directOne.svg';
import directTwo from '../../assets/directTwo.svg';
import directThree from '../../assets/directThree.svg';
import tick from '../../assets/tick-svgrepo-com.svg';
import StripeCheckout from './StripeCheckout.js';
// Stripe PaymentIntent statuses, where user actions are already completed
// https://stripe.com/docs/payments/payment-intents/status
const STRIPE_PI_USER_ACTIONS_DONE_STATUSES = ['processing', 'requires_capture', 'succeeded'];

// Payment charge options
const ONETIME_PAYMENT = 'ONETIME_PAYMENT';
const PAY_AND_SAVE_FOR_LATER_USE = 'PAY_AND_SAVE_FOR_LATER_USE';
const USE_SAVED_CARD = 'USE_SAVED_CARD';

const paymentFlow = (selectedPaymentMethod, saveAfterOnetimePayment) => {
  // Payment mode could be 'replaceCard', but without explicit saveAfterOnetimePayment flag,
  // we'll handle it as one-time payment
  return selectedPaymentMethod === 'defaultCard'
    ? USE_SAVED_CARD
    : saveAfterOnetimePayment
    ? PAY_AND_SAVE_FOR_LATER_USE
    : ONETIME_PAYMENT;
};

/**
 * Construct orderParams object using pageData from session storage, shipping details, and optional payment params.
 * Note: This is used for both speculate transition and real transition
 *       - Speculate transition is called, when the the component is mounted. It's used to test if the data can go through the API validation
 *       - Real transition is made, when the user submits the StripePaymentForm.
 *
 * @param {Object} pageData data that's saved to session storage.
 * @param {Object} shippingDetails shipping address if applicable.
 * @param {Object} optionalPaymentParams (E.g. paymentMethod or setupPaymentMethodForSaving)
 * @param {Object} config app-wide configs. This contains hosted configs too.
 * @returns orderParams.
 */
const getOrderParams = (pageData, shippingDetails, optionalPaymentParams, config) => {
  const quantity = pageData.orderData?.quantity;
  const quantityMaybe = quantity ? { quantity } : {};
  const deliveryMethod = pageData.orderData?.deliveryMethod;
  const deliveryMethodMaybe = deliveryMethod ? { deliveryMethod } : {};
  const author = pageData?.listing?.author;
  const authorId = author?.id?.uuid;
  const isPopNouvelleUser = authorId === process.env.REACT_APP_POP_NOUVELLE_USER;
  const { listingType, unitType, created_by } = pageData?.listing?.attributes?.publicData || {};
  const isCreatedByUsingScript = created_by === 'script';
  const protectedDataMaybe = {
    protectedData: {
      ...getTransactionTypeData(listingType, unitType, config),
      ...deliveryMethodMaybe,
      ...shippingDetails,
      isPopNouvelleUser,
      isCreatedByUsingScript,
    },
  };

  // These are the order parameters for the first payment-related transition
  // which is either initiate-transition or initiate-transition-after-enquiry
  const orderParams = {
    listingId: pageData?.listing?.id,
    authorId,
    ...deliveryMethodMaybe,
    ...quantityMaybe,
    ...bookingDatesMaybe(pageData.orderData?.bookingDates),
    ...protectedDataMaybe,
    ...optionalPaymentParams,
    isPopNouvelleUser,
    isCreatedByUsingScript,
  };
  return orderParams;
};

const fetchSpeculatedTransactionIfNeeded = (
  orderParams,
  pageData,
  fetchSpeculatedTransaction,
  promoCode
) => {
  const tx = pageData ? pageData.transaction : null;
  const pageDataListing = pageData.listing;
  const processName =
    tx?.attributes?.processName ||
    pageDataListing?.attributes?.publicData?.transactionProcessAlias?.split('/')[0];
  const process = processName ? getProcess(processName) : null;

  // If transaction has passed payment-pending state, speculated tx is not needed.
  const shouldFetchSpeculatedTransaction =
    !!pageData?.listing?.id &&
    !!pageData.orderData &&
    !!process &&
    !hasTransactionPassedPendingPayment(tx, process);
  console.log('hello from ---shouldFetchSpeculatedTransaction', shouldFetchSpeculatedTransaction);

  if (shouldFetchSpeculatedTransaction) {
    const processAlias = pageData.listing.attributes.publicData?.transactionProcessAlias;
    const transactionId = tx ? tx.id : null;
    const isInquiryInPaymentProcess =
      tx?.attributes?.lastTransition === process.transitions.INQUIRE;

    const requestTransition = isInquiryInPaymentProcess
      ? process.transitions.REQUEST_PAYMENT_AFTER_INQUIRY
      : process.transitions.REQUEST_PAYMENT;
    const isPrivileged = process.isPrivileged(requestTransition);

    fetchSpeculatedTransaction(
      orderParams,
      processAlias,
      transactionId,
      requestTransition,
      isPrivileged,
      promoCode
    );
  }
};

/**
 * Load initial data for the page
 *
 * Since the data for the checkout is not passed in the URL (there
 * might be lots of options in the future), we must pass in the data
 * some other way. Currently the ListingPage sets the initial data
 * for the CheckoutPage's Redux store.
 *
 * For some cases (e.g. a refresh in the CheckoutPage), the Redux
 * store is empty. To handle that case, we store the received data
 * to window.sessionStorage and read it from there if no props from
 * the store exist.
 *
 * This function also sets of fetching the speculative transaction
 * based on this initial data.
 */
export const loadInitialDataForStripePayments = ({
  pageData,
  fetchSpeculatedTransaction,
  fetchStripeCustomer,
  config,
  promoCode = false,
}) => {
  // Fetch currentUser with stripeCustomer entity
  // Note: since there's need for data loading in "componentWillMount" function,
  //       this is added here instead of loadData static function.
  fetchStripeCustomer();

  console.log('loAD----');
  // Fetch speculated transaction for showing price in order breakdown
  // NOTE: if unit type is line-item/item, quantity needs to be added.
  // The way to pass it to checkout page is through pageData.orderData
  const shippingDetails = {};
  const optionalPaymentParams = {};
  const orderParams = getOrderParams(pageData, shippingDetails, optionalPaymentParams, config);

  fetchSpeculatedTransactionIfNeeded(orderParams, pageData, fetchSpeculatedTransaction, promoCode);
};

const handleSubmit = (values, process, props, stripe, submitting, setSubmitting, promoCode) => {
  if (submitting) {
    return;
  }
  console.log('00000000000000------------------');
  setSubmitting(true);

  const {
    history,
    config,
    routeConfiguration,
    speculatedTransaction,
    currentUser,
    stripeCustomerFetched,
    paymentIntent,
    dispatch,
    onInitiateOrder,
    onConfirmCardPayment,
    onConfirmPayment,
    onSendMessage,
    onSavePaymentMethod,
    onSubmitCallback,
    pageData,
    setPageData,
    sessionStorageKey,
  } = props;

  const { card, message, paymentMethod: selectedPaymentMethod, formValues } = values;
  const { saveAfterOnetimePayment: saveAfterOnetimePaymentRaw } = formValues;

  const saveAfterOnetimePayment =
    Array.isArray(saveAfterOnetimePaymentRaw) && saveAfterOnetimePaymentRaw.length > 0;
  const selectedPaymentFlow = paymentFlow(selectedPaymentMethod, saveAfterOnetimePayment);
  const hasDefaultPaymentMethodSaved = hasDefaultPaymentMethod(stripeCustomerFetched, currentUser);
  const stripePaymentMethodId = hasDefaultPaymentMethodSaved
    ? currentUser?.stripeCustomer?.defaultPaymentMethod?.attributes?.stripePaymentMethodId
    : null;

  // Track add_payment_info once when the user proceeds to payment.
  const listing = pageData?.listing;
  const payinTotal = speculatedTransaction?.attributes?.payinTotal;
  const unitPrice = listing?.attributes?.price
    ? convertMoneyToNumber(listing.attributes.price)
    : 0;
  const item = listing ? formatListingForAnalytics(listing, config) : null;
  const paymentTypeMap = {
    [USE_SAVED_CARD]: 'saved_card',
    [PAY_AND_SAVE_FOR_LATER_USE]: 'card',
    [ONETIME_PAYMENT]: 'card',
  };
  trackEvent('add_payment_info', {
    currency: payinTotal?.currency || config.currency,
    value: payinTotal ? payinTotal.amount / 100 : unitPrice,
    payment_type: paymentTypeMap[selectedPaymentFlow] || 'card',
    items: item ? [item] : [],
  });

  // If paymentIntent status is not waiting user action,
  // confirmCardPayment has been called previously.
  const hasPaymentIntentUserActionsDone =
    paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

  const requestPaymentParams = {
    pageData,
    speculatedTransaction,
    stripe,
    card,
    billingDetails: getBillingDetails(formValues, currentUser),
    message,
    paymentIntent,
    hasPaymentIntentUserActionsDone,
    stripePaymentMethodId,
    process,
    onInitiateOrder,
    onConfirmCardPayment,
    onConfirmPayment,
    onSendMessage,
    onSavePaymentMethod,
    sessionStorageKey,
    stripeCustomer: currentUser?.stripeCustomer,
    isPaymentFlowUseSavedCard: selectedPaymentFlow === USE_SAVED_CARD,
    isPaymentFlowPayAndSaveCard: selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE,
    setPageData,
  };
  const shippingType = formValues.shippingType;
  const viber = formValues.viber;

  const shippingDetails = getShippingDetailsMaybe(formValues);
  const shippingData = getShippingDetailsForSave(formValues);

  // Note: optionalPaymentParams contains Stripe paymentMethod,
  // but that can also be passed on Step 2
  // stripe.confirmCardPayment(stripe, { payment_method: stripePaymentMethodId })
  const optionalPaymentParams =
    selectedPaymentFlow === USE_SAVED_CARD && hasDefaultPaymentMethodSaved
      ? { paymentMethod: stripePaymentMethodId }
      : selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE
      ? { setupPaymentMethodForSaving: true }
      : {};

  // These are the order parameters for the first payment-related transition
  // which is either initiate-transition or initiate-transition-after-enquiry
  const orderParams = getOrderParams(pageData, shippingDetails, optionalPaymentParams, config);

  // There are multiple XHR calls that needs to be made against Stripe API and Sharetribe Marketplace API on checkout with payments
  processCheckoutWithPayment(
    orderParams,
    requestPaymentParams,
    shippingType,
    shippingData,
    viber,
    history,
    promoCode
  )
    .then(response => {
      const { orderId, messageSuccess, paymentMethodSaved, clientSecret } = response;
      setSubmitting(false);
      if (!clientSecret) {
        const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
        const orderDetailsPath = pathByRouteName('OrderDetailsPage', routeConfiguration, {
          id: orderId?.uuid,
        });
        const initialValues = {
          initialMessageFailedToTransaction,
          savePaymentMethodFailed: !paymentMethodSaved,
        };

        setOrderPageInitialValues(initialValues, routeConfiguration, dispatch);
        onSubmitCallback();
        history.push(orderDetailsPath);
      }
    })
    .catch(err => {
      console.error(err);
      setSubmitting(false);
    });
};

const onStripeInitialized = (stripe, process, props) => {
  const { paymentIntent, onRetrievePaymentIntent, pageData } = props;
  const tx = pageData?.transaction || null;

  // We need to get up to date PI, if payment is pending but it's not expired.
  const shouldFetchPaymentIntent =
    stripe &&
    !paymentIntent &&
    tx?.id &&
    process?.getState(tx) === process?.states.PENDING_PAYMENT &&
    !hasPaymentExpired(tx, process);

  if (shouldFetchPaymentIntent) {
    const { stripePaymentIntentClientSecret } =
      tx.attributes.protectedData?.stripePaymentIntents?.default || {};

    // Fetch up to date PaymentIntent from Stripe
    onRetrievePaymentIntent({ stripe, stripePaymentIntentClientSecret });
  }
};

export const CheckoutPageWithPayment = props => {
  const [submitting, setSubmitting] = useState(false);
  // Initialized stripe library is saved to state - if it's needed at some point here too.
  const [stripe, setStripe] = useState(null);
  const [ispopNuser, setIspopNuser] = useState(false);

  const {
    scrollingDisabled,
    speculateTransactionError,
    speculatedTransaction: speculatedTransactionMaybe,
    initiateOrderError,
    confirmPaymentError,
    intl,
    currentUser,
    confirmCardPaymentError,
    paymentIntent,
    retrievePaymentIntentError,
    stripeCustomerFetched,
    pageData,
    processName,
    listingTitle,
    title,
    config,
    onSpeculateTransactionShipping,
    shippingTypeInProgress,
    onManageDisableScrolling,
    privacyAssetsData,
    privacyFetchInProgress,
    privacyFetchError,
    tosAssetsData,
    tosFetchInProgress,
    tosFetchError,
    stripeCheckoutSecret,
    setPromoCode,
    promoCode,
    fetchStripeCustomer,
    fetchSpeculatedTransaction,
    dispatch,

    invalidPromoCode,
    ispromoCodeApplied,
    applyPromoCodeInProgress,
    userHasFirstTransaction,
  } = props;
  const [showShippingDetails, setShowShippingDetails] = useState(false);
  const [showAuthenticatedShipping, setShowAuthenticatedShipping] = useState(false);
  const [selectedRecipientCountry, setSelectedRecipientCountry] = useState(null);

  const handleShowShippingDetails = state => {
    setShowShippingDetails(state);
  };
  const handleShowAuthenticatedShipping = state => {
    setShowAuthenticatedShipping(state);
  };
  // Since the listing data is already given from the ListingPage
  // and stored to handle refreshes, it might not have the possible
  // deleted or closed information in it. If the transaction
  // initiate or the speculative initiate fail due to the listing
  // being deleted or closed, we should dig the information from the
  // errors and not the listing data.
  const listingNotFound =
    isTransactionInitiateListingNotFoundError(speculateTransactionError) ||
    isTransactionInitiateListingNotFoundError(initiateOrderError);

  const { listing, transaction, orderData } = pageData;
  const existingTransaction = ensureTransaction(transaction);
  const speculatedTransaction = ensureTransaction(speculatedTransactionMaybe, {}, null);

  // If existing transaction has line-items, it has gone through one of the request-payment transitions.
  // Otherwise, we try to rely on speculatedTransaction for order breakdown data.
  const tx =
    existingTransaction?.attributes?.lineItems?.length > 0
      ? existingTransaction
      : speculatedTransaction;
  const timeZone = listing?.attributes?.availabilityPlan?.timezone;
  const transactionProcessAlias = listing?.attributes?.publicData?.transactionProcessAlias;
  const unitType = listing?.attributes?.publicData?.unitType;
  const lineItemUnitType = `line-item/${unitType}`;
  const dateType = lineItemUnitType === LINE_ITEM_HOUR ? DATE_TYPE_DATETIME : DATE_TYPE_DATE;
  const txBookingMaybe = tx?.booking?.id ? { booking: tx.booking, dateType, timeZone } : {};

  // Show breakdown only when (speculated?) transaction is loaded
  // (i.e. it has an id and lineItems)
  const breakdown =
    tx.id && tx.attributes.lineItems?.length > 0 ? (
      shippingTypeInProgress ? (
        <div className={css.shippingLoading}>
          <IconSpinner />
        </div>
      ) : (
        <>
          <OrderBreakdown
            className={css.orderBreakdown}
            userRole="customer"
            transaction={tx}
            {...txBookingMaybe}
            currency={config.currency}
            marketplaceName={config.marketplaceName}
            shippingTypeInProgress={shippingTypeInProgress}
            setPromoCode={setPromoCode}
            promoCode={promoCode}
            fetchStripeCustomer={fetchStripeCustomer}
            pageData={pageData}
            config={config}
            fetchSpeculatedTransaction={fetchSpeculatedTransaction}
            checkoutPage={true}
            currentUser={currentUser}
            hideShippingFee={!selectedRecipientCountry}
            stripeCheckoutSecret={stripeCheckoutSecret}
            invalidPromoCode={invalidPromoCode}
            ispromoCodeApplied={ispromoCodeApplied}
            applyPromoCodeInProgress={applyPromoCodeInProgress}
            userHasFirstTransaction={userHasFirstTransaction}
          />
        </>
      )
    ) : null;

  const totalPrice =
    tx?.attributes?.lineItems?.length > 0 ? getFormattedTotalPrice(tx, intl) : null;

  const process = processName ? getProcess(processName) : null;
  const transitions = process.transitions;
  const isPaymentExpired = hasPaymentExpired(existingTransaction, process);
  const isListingClosed = listing?.attributes?.state === 'closed';

  // Allow showing page when currentUser is still being downloaded,
  // but show payment form only when user info is loaded.
  const showPaymentForm = !!(
    currentUser &&
    !listingNotFound &&
    !isListingClosed &&
    !initiateOrderError &&
    !speculateTransactionError &&
    !retrievePaymentIntentError &&
    !isPaymentExpired
  );

  const firstImage = listing?.images?.length > 0 ? listing.images[0] : null;

  const listingLink = (
    <NamedLink
      name="ListingPage"
      params={{ id: listing?.id?.uuid, slug: createSlug(listingTitle) }}
    >
      <FormattedMessage id="CheckoutPage.errorlistingLinkText" />
    </NamedLink>
  );

  const errorMessages = getErrorMessages(
    listingNotFound,
    initiateOrderError,
    isPaymentExpired,
    retrievePaymentIntentError,
    speculateTransactionError,
    listingLink,
    isListingClosed
  );

  const txTransitions = existingTransaction?.attributes?.transitions || [];
  const hasInquireTransition = txTransitions.find(tr => tr.transition === transitions.INQUIRE);
  const showInitialMessageInput = !hasInquireTransition;

  // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
  const userName = currentUser?.attributes?.profile
    ? `${currentUser.attributes.profile.firstName} `
    : null;
  const surname = currentUser?.attributes?.profile
    ? `${currentUser.attributes.profile.lastName}`
    : null;
  const publicData = currentUser?.attributes?.profile?.publicData;
  const { viber = true } = publicData || {};
  // If paymentIntent status is not waiting user action,
  // confirmCardPayment has been called previously.
  const hasPaymentIntentUserActionsDone =
    paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

  // If your marketplace works mostly in one country you can use initial values to select country automatically
  // e.g. {country: 'FI'}

  const initalValuesForStripePayment = {
    name: userName,
    recipientName: userName,
    shippingType: 'direct',
    recipientCountry: undefined,
    recipientSurname: surname,
    viber,
  };
  const askShippingDetails =
    orderData?.deliveryMethod === 'shipping' &&
    !hasTransactionPassedPendingPayment(existingTransaction, process);

  return (
    <Page title={title} scrollingDisabled={scrollingDisabled}>
      <CustomTopbar intl={intl} />
      <div className={css.contentContainer}>
        <MobileListingImage
          listingTitle={listingTitle}
          author={listing?.author}
          firstImage={firstImage}
          layoutListingImageConfig={config.layout.listingImage}
        />
        <div className={css.orderFormContainer}>
          <div className={css.headingContainer}>
            <H3 as="h1" className={css.heading}>
              {title}
            </H3>
            <H4 as="h2" className={css.detailsHeadingMobile}>
              <FormattedMessage id="CheckoutPage.listingTitle" values={{ listingTitle }} />
            </H4>
          </div>
          <MobileOrderBreakdown
            speculateTransactionErrorMessage={errorMessages.speculateTransactionErrorMessage}
            breakdown={breakdown}
          />
          <section className={css.paymentContainer}>
            {errorMessages.initiateOrderErrorMessage}
            {errorMessages.listingNotFoundErrorMessage}
            {errorMessages.speculateErrorMessage}
            {errorMessages.retrievePaymentIntentErrorMessage}
            {errorMessages.paymentExpiredMessage}

            {showPaymentForm && !stripeCheckoutSecret ? (
              <StripePaymentForm
                className={css.paymentForm}
                onSubmit={values =>
                  handleSubmit(values, process, props, stripe, submitting, setSubmitting, promoCode)
                }
                inProgress={submitting}
                setPromoCode={setPromoCode}
                ispromoCodeApplied={ispromoCodeApplied}
                fetchStripeCustomer={fetchStripeCustomer}
                config={config}
                fetchSpeculatedTransactionNew={fetchSpeculatedTransaction}
                checkoutPage={true}
                currentUser={currentUser}
                stripeCheckoutSecret={stripeCheckoutSecret}
                setIspopNuser={setIspopNuser}
                dispatch={dispatch}
                formId="CheckoutPagePaymentForm"
                authorDisplayName={listing?.author?.attributes?.profile?.displayName}
                showInitialMessageInput={showInitialMessageInput}
                initialValues={initalValuesForStripePayment}
                fetchSpeculatedTransaction={onSpeculateTransactionShipping}
                promoCode={promoCode}
                initiateOrderError={initiateOrderError}
                confirmCardPaymentError={confirmCardPaymentError}
                pageData={pageData}
                confirmPaymentError={confirmPaymentError}
                hasHandledCardPayment={hasPaymentIntentUserActionsDone}
                loadingData={!stripeCustomerFetched}
                applyPromoCodeInProgress={applyPromoCodeInProgress}
                defaultPaymentMethod={
                  hasDefaultPaymentMethod(stripeCustomerFetched, currentUser)
                    ? currentUser.stripeCustomer.defaultPaymentMethod
                    : null
                }
                paymentIntent={paymentIntent}
                handleShowShippingDetails={handleShowShippingDetails}
                handleShowAuthenticatedShipping={handleShowAuthenticatedShipping}
                onStripeInitialized={stripe => {
                  setStripe(stripe);
                  return onStripeInitialized(stripe, process, props);
                }}
                askShippingDetails={askShippingDetails}
                showPickUplocation={orderData?.deliveryMethod === 'pickup'}
                listingLocation={listing?.attributes?.publicData?.location}
                totalPrice={totalPrice}
                locale={config.localization.locale}
                stripePublishableKey={config.stripe.publishableKey}
                marketplaceName={config.marketplaceName}
                isBooking={isBookingProcessAlias(transactionProcessAlias)}
                isFuzzyLocation={config.maps.fuzzy.enabled}
                onManageDisableScrolling={onManageDisableScrolling}
                onRecipientCountryChange={setSelectedRecipientCountry}
              />
            ) : null}
            {stripeCheckoutSecret ? <StripeCheckout secret={stripeCheckoutSecret} /> : null}
          </section>
        </div>

        <div className={css.detailsSideCardWrapper}>
          <DetailsSideCard
            listing={listing}
            listingTitle={listingTitle}
            author={listing?.author}
            firstImage={firstImage}
            layoutListingImageConfig={config.layout.listingImage}
            speculateTransactionErrorMessage={errorMessages.speculateTransactionErrorMessage}
            isInquiryProcess={false}
            processName={processName}
            breakdown={breakdown}
            intl={intl}
          />
        </div>
      </div>
      <Modal
        {...props}
        isOpen={showShippingDetails}
        onClose={() => {
          handleShowShippingDetails(false);
        }}
        isTopbar={true}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div style={{ margin: '1rem' }}>
          <h2 className={css.popUpHeading}>
            {intl.formatMessage({
              id: 'CheckoutPage.CheckoutPageWithPayment.directShippingHeading',
            })}
          </h2>
          <p>
            {intl.formatMessage({
              id: 'CheckoutPage.CheckoutPageWithPayment.directShippingSubHeading',
            })}
          </p>
          <ul className={css.ul}>
            <li className={css.li}>
              <img src={directOne} alt="logo" className={css.directShipping} />
              <FormattedMessage
                id="CheckoutPage.CheckoutPageWithPayment.directShippingNoteONe"
                values={{ b: msg => <b className={css.bold}>{msg}</b> }}
              />
            </li>
            <li className={css.li}>
              <img src={directTwo} alt="logo" className={css.directShipping} />
              <FormattedMessage
                id="CheckoutPage.CheckoutPageWithPayment.directNoteTwo"
                values={{ b: msg => <b className={css.bold}>{msg}</b> }}
              />
            </li>
            <li className={css.li}>
              <img src={directThree} alt="logo" className={css.directShipping} />
              <FormattedMessage
                id="CheckoutPage.CheckoutPageWithPayment.directNoteThree"
                values={{ b: msg => <b className={css.bold}>{msg}</b> }}
              />
            </li>
          </ul>
        </div>
      </Modal>
      <Modal
        {...props}
        isOpen={showAuthenticatedShipping}
        onClose={() => {
          handleShowAuthenticatedShipping(false);
        }}
        isTopbar={true}
        onManageDisableScrolling={onManageDisableScrolling}
      >
        {ispopNuser ? (
          <div>
            <h2 className={css.popUpHeading}>
              {intl.formatMessage({
                id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedShippingHeading',
              })}
            </h2>
            <div className={css.section}>
              <div>
                <p className={css.text}>
                  {intl.formatMessage({
                    id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedText1',
                  })}
                </p>
                <p className={css.textnew}>
                  {intl.formatMessage({
                    id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedText2',
                  })}
                </p>
                <div className={css.iconNDtext}>
                  <img src={tick} alt="logo" className={css.tick} />

                  <p className={css.textnew}>
                    <b>
                      <FormattedMessage id="CheckoutPage.CheckoutPageWithPayment.authenticatedPoint1bold" />
                    </b>
                    {intl.formatMessage({
                      id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedPoint1',
                    })}
                  </p>
                </div>
                <div className={css.iconNDtext}>
                  <img src={tick} alt="logo" className={css.tick} />

                  <p className={css.textnew}>
                    <b>
                      <FormattedMessage id="CheckoutPage.CheckoutPageWithPayment.authenticatedPoint2bold" />
                    </b>

                    {intl.formatMessage({
                      id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedPoint2',
                    })}
                  </p>
                </div>
                <div className={css.iconNDtext}>
                  <img src={tick} alt="logo" className={css.tick} />

                  <p className={css.text}>
                    <b>
                      <FormattedMessage id="CheckoutPage.CheckoutPageWithPayment.authenticatedPoint3bold" />
                    </b>
                    {intl.formatMessage({
                      id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedPoint3',
                    })}
                  </p>
                </div>

                <p className={css.text}>
                  {intl.formatMessage({
                    id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedText3',
                  })}
                </p>
                <p className={css.text}>
                  {intl.formatMessage({
                    id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedText4',
                  })}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h2 className={css.popUpHeading}>
              {intl.formatMessage({
                id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedShippingHeading',
              })}
            </h2>
            <div className={css.section}>
              <div className={css.iconContainer}>
                <IoShieldCheckmark className={css.iconModal} />
              </div>
              <div>
                <h3 className={css.modalSubtitle}>
                  {intl.formatMessage({
                    id: 'CheckoutPage.CheckoutPageWithPayment.authencticatedSubheading',
                  })}
                </h3>
                <p className={css.text}>
                  {intl.formatMessage({
                    id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedText11',
                  })}
                </p>
              </div>
            </div>
            <div className={css.sectionn}>
              <div className={css.iconContainer}>
                <img className={css.refundImage} src={refundImage} />
              </div>
              <div>
                <h3 className={css.modalSubtitle}>
                  {intl.formatMessage({
                    id: 'CheckoutPage.CheckoutPageWithPayment.authenticatedSubheading2',
                  })}
                </h3>
                <p className={css.text}>
                  {intl.formatMessage({ id: 'CheckoutPage.CheckoutPageWithPayment.text4' })}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>
      {/* <Modal
        id="checkout.tos"
        isOpen={showTos}
        onClose={() => handleShowTos(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.termsWrapper}>
          <TermsOfServiceContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(TOS_ASSET_NAME)]?.data}
          />
        </div>
      </Modal> */}
      {/* <Modal
        id="checkout.privacyPolicy"
        isOpen={showPrivacy}
        onClose={() => handleShowPrivacy(false)}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.privacyWrapper}>
          <PrivacyPolicyContent
            inProgress={tosFetchInProgress}
            error={tosFetchError}
            data={tosAssetsData?.[camelize(PRIVACY_POLICY_ASSET_NAME)]?.data}
          />
        </div>
      </Modal> */}
    </Page>
  );
};

CheckoutPageWithPayment.defaultProps = {
  initiateOrderError: null,
  confirmPaymentError: null,
  listing: null,
  orderData: {},
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  currentUser: null,
  paymentIntent: null,
};

CheckoutPageWithPayment.propTypes = {
  scrollingDisabled: bool.isRequired,
  listing: propTypes.listing,
  orderData: object,
  fetchStripeCustomer: func.isRequired,
  stripeCustomerFetched: bool.isRequired,
  fetchSpeculatedTransaction: func.isRequired,
  speculateTransactionInProgress: bool.isRequired,
  speculateTransactionError: propTypes.error,
  speculatedTransaction: propTypes.transaction,
  transaction: propTypes.transaction,
  currentUser: propTypes.currentUser,
  params: shape({
    id: string,
    slug: string,
  }).isRequired,
  onConfirmPayment: func.isRequired,
  onInitiateOrder: func.isRequired,
  onConfirmCardPayment: func.isRequired,
  onRetrievePaymentIntent: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onSendMessage: func.isRequired,
  initiateOrderError: propTypes.error,
  confirmPaymentError: propTypes.error,
  // confirmCardPaymentError comes from Stripe so that's why we can't expect it to be in a specific form
  confirmCardPaymentError: oneOfType([propTypes.error, object]),
  paymentIntent: object,

  // from connect
  dispatch: func.isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

export default CheckoutPageWithPayment;
